import { createContext, useContext, useMemo } from "react";
import { useLocation } from "wouter";

interface WorkspaceContext {
  workspaceSlug: string;
  workspacePrefix: string;
  buildPath: (path: string) => string;
}

const WorkspaceContext = createContext<WorkspaceContext | null>(null);

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    // Task #107 — Fail closed. We used to silently default to
    // `warehouse-productions` here, which caused workspace-scoped writes
    // made from non-/w routes (or before the provider mounted) to leak
    // into the wrong tenant. Try to recover the slug from the current URL;
    // if there isn't one, return an explicit unresolved sentinel so
    // `buildPath` produces a clearly broken URL the server will reject
    // with WORKSPACE_REQUIRED instead of silently writing to a default
    // tenant.
    const fromUrl = getWorkspaceFromUrl();
    if (fromUrl) {
      return {
        workspaceSlug: fromUrl,
        workspacePrefix: `/w/${fromUrl}`,
        buildPath: (path: string) =>
          `/w/${fromUrl}${path.startsWith('/') ? path : '/' + path}`,
      };
    }
    return {
      workspaceSlug: "__no_workspace__",
      workspacePrefix: "/w/__no_workspace__",
      buildPath: (path: string) =>
        `/w/__no_workspace__${path.startsWith('/') ? path : '/' + path}`,
    };
  }
  return context;
}

interface WorkspaceProviderProps {
  workspaceSlug: string;
  children: React.ReactNode;
}

export function WorkspaceProvider({ workspaceSlug, children }: WorkspaceProviderProps) {
  const value = useMemo(() => ({
    workspaceSlug,
    workspacePrefix: `/w/${workspaceSlug}`,
    buildPath: (path: string) => `/w/${workspaceSlug}${path.startsWith('/') ? path : '/' + path}`,
  }), [workspaceSlug]);

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

// Extract workspace slug from current URL
export function getWorkspaceFromUrl(): string | null {
  const path = window.location.pathname;
  const match = path.match(/^\/w\/([^\/]+)/);
  return match ? match[1] : null;
}

// Check if we're in development mode (Replit, localhost)
export function isDevelopmentMode(): boolean {
  const hostname = window.location.hostname;
  return hostname.includes("replit") || 
         hostname.includes("localhost") || 
         hostname.includes("127.0.0.1");
}
